<template>
  <div class="main-container">
    <loading :active.sync="isLoading" :is-full-page="true" color="#4e97b2"></loading>
    <div class="container text-left">
      <BreadcrumbsHelper :breadcrumbs="this.breadcrumbs" />
      <div v-if="this.city" class="edit-container">
        <div>
          <h1>
            {{ this.$store.state.auth.mUser.role === 'admin' ? 'Edit' : 'Suggest an Edit to' }}
            {{ city.name }}
          </h1>
          <div class="ml-3">
            <ProgressTracker :nodes="pages" :currentStep="currentStep" @stepClicked="goToPage" />
          </div>
          <!-- Step 1 -->
          <section v-if="this.pages[this.currentStep].id === 'General'">
            <div class="edit-section-header">
              <h1>Location</h1>
            </div>
            <div v-if="this.$store.state.auth.mUser.role === 'admin'" class="input-wrapper">
              <p class="input-label">City Type</p>
              <p class="input-label-secondary">Cities are classified as major, minor and coastal.</p>
              <select name="cityType" id="cityType" v-model="city.city_type">
                <option value="0">Major City</option>
                <option value="2">Minor City</option>
              </select>
            </div>
            <div class="input-wrapper">
              <p class="input-label">City Description</p>
              <froala :config="config" v-model="city.description" />
              <p class="validation-error">
                <span v-if="$v.city.description.$error">Required.</span>
              </p>
            </div>
          </section>
          <!-- Step 2 -->
          <section v-if="this.pages[this.currentStep].id === 'Media'">
            <div class="edit-section-header">
              <h1>Images</h1>
              <h5>
                For city pages, it's essential to have a captivating visual representation. Please ensure that you upload at least one image that will be prominently displayed as
                the featured image on the city page. If you're adding multiple images, make sure to designate one as the 'featured' image, while the remaining will be accessible in
                a gallery modal for viewers to explore. We welcome a diverse range of photos to showcase the city's charm. Currently, we accept only jpg, jpeg, and png formats.
              </h5>
            </div>
            <div class="media-upload-wrapper">
              <FileInput :allow-multiple-images="true" @files-uploaded="onCityImagesUploaded($event)" />
            </div>
            <div class="media-image-display">
              <CityImageDisplay
                title="Featured Image"
                v-bind:photos.sync="city.photos"
                :cityId="city.id"
                :validations="$v.city.photos.$each"
                @set-featured="onCityImagesSetFeatured($event)"
                @remove-image="onCityImageDeleted($event)"
              />
            </div>
          </section>
          <!-- Step 3 -->
          <section v-if="this.pages[this.currentStep].id === 'AdditionalContent'">
            <div class="edit-section-header">
              <h1>Additional Content</h1>
              <h5>Available for larger cities.</h5>
            </div>
            <div v-if="this.$store.state.auth.mUser.role === 'admin'" class="input-wrapper">
              <p class="input-label">Crowdriff ID</p>
              <input type="text" v-model="city.crowdriff_id" />
            </div>
            <div class="input-wrapper">
              <p class="input-label">Call Out Quote</p>
              <input type="text" v-model="city.insiders_quote" />
              <p class="validation-error">
                <span v-if="$v.city.insiders_quote.$error">Required.</span>
              </p>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Headline</p>
              <input type="text" v-model="city.insiders_headline" />
              <p class="validation-error">
                <span v-if="$v.city.insiders_headline.$error">Required.</span>
              </p>
            </div>
            <div class="input-wrapper">
              <p class="input-label">You Might Want To Try...</p>
              <textarea class="textarea-med" v-model="city.insiders_text"></textarea>
              <p class="validation-error">
                <span v-if="$v.city.insiders_text.$error">Required.</span>
              </p>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Insiders Guide Url</p>
              <input type="text" v-model="city.insiders_url" />
              <p class="validation-error">
                <span v-if="$v.city.insiders_url.$error">Must include http:// or https:// </span>
              </p>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Insiders Guide Link Text</p>
              <input type="text" v-model="city.insiders_guide_link_text" />
              <p class="validation-error">
                <span v-if="$v.city.insiders_guide_link_text.$error">Required.</span>
              </p>
            </div>
            <div class="">
              <div class="media-upload-wrapper">
                <FileInput @files-uploaded="onInsiderImageUploaded($event)" />
              </div>
              <div class="media-image-display">
                <CityImageDisplay
                  title="Insiders Guide Image"
                  :photos="[]"
                  :originalInsidersImage="this.city.insiders_image"
                  :newInsiderImageUrl="this.newInsiderImageUrl"
                  :cityId="city.id"
                  :insiderImageDescription="this.city.insiders_image_description"
                  @update-insiders-img-description="updateInsiderImageDescription($event)"
                  @remove-insiders-image="onInsiderGuideImageDeleted($event)"
                />
              </div>
            </div>
            <div class="edit-section-header">
              <h1>How to Get There</h1>
              <h5>General directions to the city.</h5>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Automotive</p>
              <textarea v-model="city.directions_car"></textarea>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Plane</p>
              <textarea v-model="city.directions_airplane"></textarea>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Bus</p>
              <textarea v-model="city.directions_bus"></textarea>
            </div>
          </section>
          <!-- Step 4 -->
          <section v-if="this.pages[this.currentStep].id === 'SEO'">
            <div class="edit-section-header">
              <h1>SEO</h1>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Page Title</p>
              <input type="text" v-model="city.seo_title" />
              <p class="validation-error">
                <span v-if="$v.city.seo_title.$error">Page Title is Required.</span>
              </p>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Meta Description</p>
              <textarea v-model="city.seo_meta_description"></textarea>
              <p class="validation-error">
                <span v-if="$v.city.seo_meta_description.$error">Meta Description is Required.</span>
              </p>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Schema</p>
              <textarea class="textarea-med" v-model="city.seo_schema"></textarea>
            </div>
          </section>
          <!-- Step 5 -->
          <section v-if="this.pages[this.currentStep].id === 'AdditionalFeedback'">
            <div class="edit-section-header">
              <h1>Anything else we should know?</h1>
            </div>
            <div class="input-wrapper">
              <p class="input-label">Additional Feedback</p>
              <textarea v-model="feedback" class="textarea-lg"></textarea>
            </div>
          </section>
          <div class="footer-bar">
            <template v-if="!isSubmitting">
              <router-link :to="{ name: 'city detail', params: { cityId: city._id } }">
                <b-button class="btn btn-ghost"> Cancel Changes </b-button>
              </router-link>
              <b-button v-if="this.currentStep === this.pages.length - 1" class="btn mr-3" @click="submit()">{{
                this.$store.state.auth.mUser.role === 'admin' ? 'Submit Edit' : 'Submit Suggestion'
              }}</b-button>
              <b-button
                v-else
                class="btn mr-3"
                @click="goToPage(currentStep + 1)"
                :class="{
                  disabled: this.pages[currentStep].validationGroup.$invalid,
                }"
                >Next</b-button
              >
            </template>
            <template v-if="isSubmitting">
              <b-button class="btn disabled">Submitting...</b-button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Loading from 'vue-loading-overlay';
import { required, url } from 'vuelidate/lib/validators';
import ProgressTracker from '../common/ProgressTracker.vue';
import BreadcrumbsHelper from '../common/BreadcrumbsHelper.vue';
import CityImageDisplay from './components/CityImageDisplay.vue';
import FileInput from '../../components/FileInput.vue';

export default {
  components: {
    Loading,
    ProgressTracker,
    BreadcrumbsHelper,
    CityImageDisplay,
    FileInput,
  },
  props: {
    cityId: String,
  },
  beforeDestroy() {
    // Free up memory from the temp image blob urls
    this.tempUrls.forEach((tUrl) => URL.revokeObjectURL(tUrl));
  },
  data() {
    return {
      isLoading: false,
      city: undefined,
      feedback: '',
      currentStep: 0,
      pages: [],
      breadcrumbs: [],
      baseImageUrl: process.env.VUE_APP_S3_BASE_URL,
      newInsiderImageUrl: '',
      newImg: '',
      tempUrls: [],
      filesToUpload: [],
      isSubmitting: false,
      config: {
        key: `${process.env.VUE_APP_FROALA}`,
        heightMax: 150,
        toolbarButtons: ['bold', 'italic', 'underline', '|', 'align', 'formatOL', 'formatUL', 'outdent', 'indent', '-', 'insertLink', '|', 'html', '|', 'undo', 'redo'],
        events: {},
      },
    };
  },

  validations: {
    city: {
      city_type: {
        required,
      },
      description: {
        required,
      },
      directions_car: {
        required,
      },
      insiders_quote: {
        required,
      },
      insiders_headline: {
        required,
      },
      insiders_text: {
        required,
      },
      insiders_guide_link_text: {
        required,
      },
      insiders_url: {
        url,
      },
      photos: {
        $each: {
          description: {
            required,
          },
        },
      },
      seo_title: {
        required,
      },
      seo_meta_description: {
        required,
      },
    },
    generalGroup: ['city.city_type', 'city.description'],
    mediaGroup: ['city.photos'],
    additionalContentGroup: ['city.insiders_quote', 'city.insiders_text', 'city.insiders_headline', 'city.insiders_url', 'city.insiders_guide_link_text', 'city.directions_car'],
    seoGroup: ['city.seo_title', 'city.seo_meta_description'],
  },
  async created() {
    try {
      this.isLoading = true;
      const response = await axios.get(`${process.env.VUE_APP_API_ROOT}/cities/${this.cityId}`);

      this.city = response.data.city;
      this.setBreadcrumbs();
      this.setPages();
      this.validate();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    goToPage(page) {
      this.validate();
      if (this.pages[page].active) {
        this.currentStep = page;
      }
    },
    validate() {
      this.$v.$reset();

      let invalidIndex = -1;

      // Reset active status
      this.pages.forEach((p) => (p.active = true));

      this.pages.forEach((p, index) => {
        let invalid = false;

        if (invalidIndex === -1) {
          if (p.id === 'General') {
            this.$v.generalGroup.$touch();
            invalid = this.$v.generalGroup.$invalid;
          } else if (p.id === 'Media') {
            this.$v.mediaGroup.$touch();
            invalid = this.$v.mediaGroup.$invalid;
          } else if (p.id === 'AdditionalContent') {
            this.$v.additionalContentGroup.$touch();
            invalid = this.$v.additionalContentGroup.$invalid;
          } else if (p.id === 'SEO') {
            this.$v.seoGroup.$touch();
            invalid = this.$v.seoGroup.$invalid;
          }

          if (invalid) {
            invalidIndex = index;
          }
        } else {
          p.active = false;
        }
      });
    },
    setBreadcrumbs() {
      this.breadcrumbs = [];

      if (this.$store.state.auth.mUser.role === 'admin') {
        this.breadcrumbs = this.breadcrumbs.concat([
          {
            text: 'Dashboard',
            href: '/admin',
            active: false,
          },
          {
            text: 'Content',
            active: true,
          },
        ]);
      } else if (this.$store.state.auth.mUser.role === 'editor') {
        this.breadcrumbs = this.breadcrumbs.concat([
          {
            text: 'Dashboard',
            href: '/admin',
            active: false,
          },
        ]);
      }

      this.breadcrumbs = this.breadcrumbs.concat([
        {
          text: 'Cities',
          href: '/cities',
          active: false,
        },
        {
          text: this.city.name,
          active: false,
          href: `/cities/${this.city._id}`,
        },
      ]);
    },
    setPages() {
      this.pages = [
        {
          id: 'General',
          title: 'General Info',
          active: true,
          validationGroup: this.$v.generalGroup,
        },
        {
          id: 'Media',
          title: 'Media',
          active: true,
          validationGroup: this.$v.mediaGroup,
        },
      ];

      // Only show additional content section for Major cities
      if (this.city.city_type === 0) {
        this.pages.push({
          id: 'AdditionalContent',
          title: "Add'l Content",
          active: true,
          validationGroup: this.$v.additionalContentGroup,
        });
      }

      this.pages.push({
        id: 'SEO',
        title: 'SEO Content',
        active: true,
        validationGroup: this.$v.seoGroup,
      });

      if (this.$store.state.auth.mUser.role !== 'admin') {
        this.pages.push({
          id: 'AdditionalFeedback',
          title: "Add'l Feedback",
          active: true,
        });
      }
    },
    async submit() {
      let response = null;
      let toastMessage = '';

      this.validate();

      if (this.$v.$anyError) {
        return;
      }

      this.isSubmitting = true;

      // Upload any added images to S3
      if (this.filesToUpload.length) {
        const imageUploadSuccess = await this.uploadFilesToS3(this.filesToUpload);

        if (!imageUploadSuccess) {
          return;
        }
      }

      // Save the city record or create a change request if we are not an admin
      if (this.$store.state.auth.mUser.role === 'admin') {
        response = await axios.put(`${process.env.VUE_APP_API_ROOT}/cities/${this.cityId}`, this.city);
        toastMessage = 'You have successfully edited this city';
      } else {
        response = await axios.post(`${process.env.VUE_APP_API_ROOT}/cities/${this.cityId}/createChangeRequest`, { city: this.city, feedback: this.feedback });
        toastMessage = 'You have successfully submitted an edit request for this city.';
      }

      // Free up memory from the temp image blob urls
      this.tempUrls.forEach((tUrl) => URL.revokeObjectURL(tUrl));

      this.$router.push({ name: 'city detail', params: { cityId: this.cityId } }, () => {
        setTimeout(() => {
          this.$bvToast.toast(toastMessage, {
            title: response.data.success ? 'Success' : 'Error',
            autoHideDelay: 5000,
            appendToast: true,
          });
        }, 500);
      });
    },
    onInsiderImageUploaded(files) {
      if (files) {
        files.forEach((f) => {
          const tempUrl = URL.createObjectURL(f);
          this.newInsiderImageUrl = tempUrl;
          this.city.insiders_image = f.name;
          this.city.insiders_image_description = '';
          this.city.insiders_image_updated_at = new Date();
          this.tempUrls.push(tempUrl);
          this.filesToUpload.push(f);
        });
      }
    },
    updateInsiderImageDescription(description) {
      this.city.insiders_image_description = description;
    },
    onCityImagesUploaded(files) {
      if (files) {
        files.forEach((f) => {
          const tempUrl = URL.createObjectURL(f);
          this.city.photos.push({
            id: '',
            image: f.name,
            name: '',
            description: '',
            featured: false,
            credit: '',
            created_at: new Date(),
            tempUrl,
          });
          this.tempUrls.push(tempUrl);
          this.filesToUpload.push(f);
        });
      }

      if (this.city.photos.length === 1) {
        this.city.photos[0].featured = true;
      }
    },
    onCityImagesSetFeatured(image) {
      this.city.photos.forEach((p) => {
        if (p === image) {
          p.featured = true;
        } else {
          p.featured = false;
        }
      });
    },
    onCityImageDeleted(image) {
      this.city.photos = this.city.photos.filter((x) => x !== image);

      if (this.city.photos[0] && !this.city.photos[0].featured) {
        this.city.photos[0].featured = true;
      }
    },
    onInsiderGuideImageDeleted() {
      this.city.insiders_image = '';
      this.city.insiders_image_updated_at = new Date();
      this.updateInsiderImageDescription('');
    },
    async uploadFilesToS3(files) {
      // Convert files to base64 strings, then send over to the api to upload
      const fileImages = [];

      for (let index = 0; index < files.length; index++) {
        const imageBlob = await this.toBase64(files[index]);
        fileImages.push({
          name: files[index].name,
          blob: imageBlob,
          type: files[index].type,
        });
      }

      const request = {
        filePrefix: `uploads/city/${this.city.id}`,
        images: fileImages,
      };

      const response = await axios.post(`${process.env.VUE_APP_API_ROOT}/misc/uploadImagesToS3`, request);

      if (!response.data.success) {
        this.$bvToast.toast('Something went wrong. The image upload failed. Please try again.', { title: 'Error', autoHideDelay: 5000, appendToast: true });
      }
      return response.data.success;
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.replace('data:', '').replace(/^.+,/, ''));
        reader.onerror = reject;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
$base-width: 40rem;

.main-container {
  margin-bottom: 10rem;
}

.edit-container {
  margin-top: 2rem;
  background-color: white;
  padding: 2.5rem 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;

  h1 {
    margin-top: 0;
    color: var(--blue-900);
  }
}

.edit-section-header {
  margin-bottom: 1.5rem;
  max-width: $base-width;

  b {
    font-weight: bold;
  }

  h1 {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    font-size: 1.5rem;
  }

  h5 {
    color: var(--blue-900);
    font-size: 1rem;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
}

.validation-error {
  margin: 0;
  height: 1rem;
  color: red;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  font-size: 0.9rem;
  margin-bottom: 2rem;

  &:has(> .validation-error) {
    margin-bottom: 1rem;
  }

  .input-label {
    margin-bottom: 0.5rem;
    color: black;
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  .input-label-secondary {
    color: var(--blue-900);
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  input {
    width: 28rem;
    height: 2.5rem;
    border-radius: var(--rounded);
    border-color: #919191;
    padding: 12px;
    border-width: 1px;
  }

  select {
    border-radius: var(--rounded);
    border-color: #919191;
    padding: 0px 8px;
    border-width: 1px;
    width: 10rem;
    height: 2.5rem;
  }

  textarea {
    border-color: #919191;
    resize: none;
    max-width: $base-width;
    height: 6rem;
    padding: 12px;
    border-radius: var(--rounded);
    line-height: normal;

    &.textarea-med {
      height: 10rem;
    }

    &.textarea-lg {
      height: 15rem;
      width: 100%;
    }
  }
}

.media-upload-wrapper {
  max-width: $base-width;
}

.media-image-display {
  margin-top: 4rem;
  margin-bottom: 2rem;
}

.insider-image-container {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;

  p {
    font-family: 'klinicslabbold';
    font-size: 1.125rem;
  }

  .insider-image {
    width: 12rem;
    display: flex;
    flex-direction: column;

    div {
      height: 100%;

      img {
        float: left;
        width: 13rem;
        height: 10rem;
        object-fit: cover;
      }
    }
  }

  .suggested-image {
    width: 26rem;
    display: flex;
    flex-direction: column;

    div {
      height: 100%;
    }
  }
}

.btn {
  width: 10.5rem;
  height: 2.5rem;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: default;
  }
}
</style>
